import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAccount, useChainId } from "wagmi";
import { COLLECTION_KINDS, KIND_METADATA, type CollectionKind } from "@atelier/config";
import { api } from "@/lib/api";
import { byId } from "@/lib/chains";
import { RequireAuth } from "@/components/RequireAuth";
import { CollectionHeader } from "@/components/dashboard/CollectionHeader";
import { DropRow } from "@/components/dashboard/DropRow";
import { SimpleRow } from "@/components/dashboard/SimpleRow";
import { BadgeRow } from "@/components/dashboard/BadgeRow";
import type { Collection } from "@/components/dashboard/types";

// Display order on the dashboard. Different from COLLECTION_KINDS to keep
// Drops first, then Badges (most common creator workflows).
const KIND_ORDER: CollectionKind[] = ["drop", "badge", "simple", "1of1"];

export function Dashboard() {
  const { address } = useAccount();
  const walletChainId = useChainId();
  const chain = byId(walletChainId);
  const { data, isLoading } = useQuery({
    queryKey: ["my-collections", address, walletChainId],
    queryFn: () =>
      api.get<{ items: Collection[] }>(
        `/api/collections?chain=${walletChainId}&creator=${address}&limit=100`,
      ),
    enabled: !!address,
  });

  const grouped = useMemo(() => {
    const items = data?.items ?? [];
    const out: Record<CollectionKind, Collection[]> = Object.fromEntries(
      COLLECTION_KINDS.map((k) => [k, [] as Collection[]]),
    ) as Record<CollectionKind, Collection[]>;
    for (const c of items) out[c.kind]?.push(c);
    return out;
  }, [data]);

  const explorerTokensUrl =
    chain?.explorerUrl && address
      ? `${chain.explorerUrl.replace(/\/$/, "")}/address/${address}?tab=tokens_nfts`
      : null;

  return (
    <RequireAuth>
      <div className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="mb-1 text-2xl font-bold">Your collections</h1>
        <p className="mb-3 text-sm text-muted-foreground">
          Everything you deployed on chain {walletChainId}.
        </p>
        {explorerTokensUrl && (
          <div className="mb-6 flex flex-col gap-1 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <span>
              Looking for NFTs you <em>own</em> instead of deployed? The explorer indexes every
              ERC-721 in your wallet, including badges minted to you.
            </span>
            <a
              href={explorerTokensUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost h-7 self-start whitespace-nowrap px-3 text-xs sm:self-auto"
            >
              Open in {chain?.name ?? "explorer"} ↗
            </a>
          </div>
        )}
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (data?.items ?? []).length === 0 ? (
          <div className="card">
            <p className="mb-4 text-sm text-muted-foreground">
              You haven't deployed anything yet on this chain.
            </p>
            <Link to="/create" className="btn-primary">Create a collection</Link>
          </div>
        ) : (
          <div className="space-y-8">
            {KIND_ORDER.map((kind) => {
              const items = grouped[kind];
              if (items.length === 0) return null;
              return (
                <section key={kind}>
                  <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                    {KIND_METADATA[kind].pluralLabel}{" "}
                    <span className="ml-1 text-foreground/50">({items.length})</span>
                  </h2>
                  <div className="space-y-3">
                    {items.map((c) => {
                      if (kind === "drop") return <DropRow key={c.id} c={c} />;
                      if (kind === "badge") return <BadgeRow key={c.id} c={c} />;
                      if (kind === "simple") return <SimpleRow key={c.id} c={c} />;
                      return (
                        <div key={c.id} className="card">
                          <CollectionHeader c={c} />
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </RequireAuth>
  );
}
